import type React from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './ContributorDetails.css';

interface ContributorInfo {
  avatarName: string;
  displayName: string;
  notes: string | null;
  fundedAt: string;
}

interface ContributorDetailsProps {
  isOpen: boolean;
  onClose: () => void;
  contributor: ContributorInfo | null;
}

const ContributorDetails: React.FC<ContributorDetailsProps> = ({
  isOpen,
  onClose,
  contributor,
}) => {
  const { t } = useTranslation();

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !contributor) {
    return null;
  }

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <button
      type="button"
      className="contributor-modal-overlay"
      onClick={onClose}
      aria-label={t('common.close', 'Close')}
    >
      <div
        className="contributor-modal-content"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="contributor-modal-title"
      >
        {/* Close button */}
        <button
          type="button"
          className="contributor-modal-close"
          onClick={onClose}
          aria-label={t('common.close', 'Close')}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <title>Close</title>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Icon */}
        <div className="contributor-modal-icon">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <title>Heart</title>
            <path d="M12 21l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.18L12 21z" />
          </svg>
        </div>

        {/* Title */}
        <h2 id="contributor-modal-title" className="contributor-modal-label">
          {t('sponsorPage.sponsoredBy', 'Sponsored by')}
        </h2>

        {/* Contributor name */}
        <p className="contributor-modal-name">{contributor.displayName}</p>

        {/* Avatar name */}
        <p className="contributor-modal-subtitle">
          {t('sponsorPage.forCompanion', 'for')} <strong>{contributor.avatarName}</strong>
        </p>

        {/* Note section */}
        {contributor.notes && (
          <div className="contributor-modal-notes-box">
            <h3 className="contributor-modal-notes-label">
              {t('sponsorPage.sponsorNote', "Sponsor's Note")}
            </h3>
            <p className="contributor-modal-notes-text">"{contributor.notes}"</p>
          </div>
        )}

        {/* Date */}
        <p className="contributor-modal-date">
          {t('sponsorPage.sponsoredOn', 'Sponsored on')} {formatDate(contributor.fundedAt)}
        </p>

        {/* Close button */}
        <button type="button" className="contributor-modal-btn" onClick={onClose}>
          {t('common.close', 'Close')}
        </button>
      </div>
    </button>
  );
};

export default ContributorDetails;
